import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { productListApiService } from "../../../service/admin/productApiService";
import noImage from "../../../assets/images/common/importerr-no-image-logo.png";

export default function AlibabaDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const offerIdFromQuery = searchParams.get("id") || "";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const [isVideoActive, setIsVideoActive] = useState(false);
    const videoRef = useRef(null);

    const sanitizeUrl = (u) => {
        if (!u || typeof u !== "string") return null;
        let url = u.trim();
        if (url.startsWith("//")) url = `https:${url}`;
        if (url.startsWith("http:")) url = url.replace("http:", "https:");
        // Encode unsafe path segments to avoid 403/404 in production
        try {
            const parsed = new URL(url);
            parsed.pathname = parsed.pathname
                .split("/")
                .map(seg => encodeURIComponent(decodeURIComponent(seg)))
                .join("/");
            return parsed.toString();
        } catch {
            try { return encodeURI(url); } catch { return url; }
        }
    };

    const normalizeImageUrl = (img) => {
        if (!img) return null;
        if (typeof img === "string") return sanitizeUrl(img);
        if (typeof img === "object") {
            const candidates = [
                img.url,
                img.imageUrl,
                img.image,
                img.src,
                img.size310x310URL,
                img.originalImageUrl,
                img.thumbnail,
                img.mediaUrl,
            ].filter(Boolean);
            const first = candidates.find(Boolean);
            return sanitizeUrl(first);
        }
        return null;
    };

    const normalizeVideoUrl = (v) => {
        if (!v) return null;
        if (typeof v === "string") return sanitizeUrl(v);
        if (typeof v === "object") {
            const candidates = [v.url, v.videoUrl, v.src, v.playUrl, v.m3u8, v.mp4].filter(Boolean);
            const first = candidates.find(Boolean);
            return sanitizeUrl(first);
        }
        return null;
    };

    useEffect(() => {
        if (!offerIdFromQuery) {
            setError("Missing offerId");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        productListApiService
            .getAlibabaProductDetailByOfferId(offerIdFromQuery, "en")
            .then((res) => {
                setData(res?.result?.result || res || null);
            })
            .catch((err) => {
                setError(err?.message || "Failed to fetch product details");
            })
            .finally(() => setLoading(false));
    }, [offerIdFromQuery]);

    const DetailRow = ({ label, value }) => (
        <div className="d-flex py-2 border-bottom">
            <div style={{ width: 200 }} className="text-muted">{label}</div>
            <div className="flex-1">{value ?? "N/A"}</div>
        </div>
    );

    // Text utilities
    const containsCJK = (s) => /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/.test(String(s || ""));
    const getBestText = (...candidates) => {
        for (const c of candidates) {
            if (!c) continue;
            const str = String(c).trim();
            if (!str) continue;
            if (!containsCJK(str)) return str;
        }
        for (const c of candidates) {
            if (c && String(c).trim()) return String(c).trim();
        }
        return "N/A";
    };

    // Memoize attributes to fix dependency warnings
    const attributes = useMemo(() => data?.attributes || data?.attributeList || [], [data]);

    const productImageTrans = Array.isArray(data?.productImageTrans) ? data.productImageTrans : [];
    const normalizedProductTrans = productImageTrans.map(normalizeImageUrl).filter(Boolean);

    const imagesRaw = Array.isArray(data?.images) ? data.images : (data?.imageList || []);
    const normalizedImages = imagesRaw.map(normalizeImageUrl).filter(Boolean);

    const extraImageSources = [
        normalizeImageUrl(data?.productImage),
        normalizeImageUrl(data?.image),
        normalizeImageUrl(data?.mainImage),
        normalizeImageUrl(data?.mainImageUrl),
    ].filter(Boolean);

    // Deep scan for media URLs
    const deepImages = new Set();
    const deepVideos = new Set();
    const visit = (val, depth = 0) => {
        if (depth > 3 || !val) return;
        if (typeof val === "string") {
            const s = val.toLowerCase();
            if (/(\.jpg|\.jpeg|\.png|\.gif|\.webp)(\?|$)/.test(s) || s.includes("/image") || s.includes("/img/")) {
                const u = normalizeImageUrl(val);
                if (u) deepImages.add(u);
            }
            if (/(\.mp4|\.webm|\.m3u8)(\?|$)/.test(s) || s.includes("/video/")) {
                const u = normalizeVideoUrl(val);
                if (u) deepVideos.add(u);
            }
            return;
        }
        if (Array.isArray(val)) {
            val.forEach((v) => visit(v, depth + 1));
            return;
        }
        if (typeof val === "object") {
            Object.values(val).forEach((v) => visit(v, depth + 1));
        }
    };
    visit(data);

    const images = Array.from(new Set([
        ...normalizedProductTrans,
        ...normalizedImages,
        ...extraImageSources,
        ...Array.from(deepImages),
    ]));

    // Translate images via backend service and swap URLs
    const [translatedImages, setTranslatedImages] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!images.length || !offerIdFromQuery) { setTranslatedImages([]); return; }
                const cache = new Map();
                const fetchOne = async (img) => {
                    if (cache.has(img)) return cache.get(img);
                    try {
                        const resp = await fetch('https://shop.fulfillmen.com/Alibaba/HttpHandlers/ProductDetail.ashx?action=getimgtranslate', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ imgURL: img, offerId: offerIdFromQuery })
                        });
                        let text = '';
                        try { text = await resp.text(); } catch { text = ''; }
                        let url = '';
                        try { const j = JSON.parse(text); url = j?.data || j?.result || j?.url || j?.imageUrl || ''; } catch { /* not JSON */ }
                        if (!url) {
                            const m = String(text).match(/https?:[^"'\s<>]+/i);
                            url = m ? m[0] : '';
                        }
                        const out = sanitizeUrl(url) || img;
                        cache.set(img, out);
                        return out;
                    } catch { cache.set(img, img); return img; }
                };
                const results = await Promise.all(images.map(fetchOne));
                if (!cancelled) setTranslatedImages(results);
            } catch {
                if (!cancelled) setTranslatedImages([]);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images.join('|'), offerIdFromQuery]);

    const effectiveImages = translatedImages.length === images.length && images.length > 0
        ? translatedImages
        : images;

    // Reset active image index when images change
    useEffect(() => {
        if (images.length > 0) {
            setActiveImageIdx(0);
            setIsVideoActive(false);
        }
    }, [images.length]);

    const videos = Array.from(new Set([
        normalizeVideoUrl(data?.video),
        normalizeVideoUrl(data?.videoUrl),
        normalizeVideoUrl(data?.playUrl),
        ...(Array.isArray(data?.videoList) ? data.videoList.map(normalizeVideoUrl) : []),
        ...(Array.isArray(data?.productVideo) ? data.productVideo.map(normalizeVideoUrl) : [normalizeVideoUrl(data?.productVideo)]),
        ...(Array.isArray(data?.productVideoTrans) ? data.productVideoTrans.map(normalizeVideoUrl) : [normalizeVideoUrl(data?.productVideoTrans)]),
        ...(Array.isArray(data?.productVideoList) ? data.productVideoList.map(normalizeVideoUrl) : []),
        ...(Array.isArray(data?.videos) ? data.videos.map(normalizeVideoUrl) : []),
        ...Array.from(deepVideos),
    ].filter(Boolean)));

    // Description extraction
    const descriptionText = useMemo(() => {
        const candidates = [
            data?.productDescTrans,
            data?.productDescriptionTrans,
            data?.descriptionTrans,
            data?.detailTrans,
            data?.productDesc,
            data?.description,
            data?.detail,
        ];
        const raw = getBestText(...candidates);
        if (!raw || raw === 'N/A') return '';
        const text = String(raw)
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/\s+/g, ' ')
            .trim();
        return text;
    }, [data]);

    // Description sanitized HTML (allow images to render full width)
    const descriptionHtml = useMemo(() => {
        const htmlCandidates = [
            data?.productDescTrans,
            data?.productDescriptionTrans,
            data?.descriptionTrans,
            data?.detailTrans,
            data?.productDesc,
            data?.description,
            data?.detail,
        ];
        const raw = htmlCandidates.find(v => typeof v === 'string' && /<[^>]+>/.test(v || '')) || '';
        if (!raw) return '';
        let safe = String(raw)
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/on\w+\s*=\s*"[^"]*"/gi, '')
            .replace(/on\w+\s*=\s*'[^']*'/gi, '')
            .replace(/on\w+\s*=\s*[^\s>]+/gi, '');
        // Normalize <img> tags: prefer data-src/data-original if present, enforce https and full-width
        safe = safe.replace(/<img([^>]*)>/gi, (_m, attrs) => {
            const getAttr = (name) => {
                const re = new RegExp(name + "\\s*=\\s*(\"([^\"]*)\"|'([^']*)'|([^\\s>]+))", 'i');
                const match = attrs.match(re);
                return match ? (match[2] || match[3] || match[4] || '') : '';
            };
            let src = getAttr('src') || getAttr('data-src') || getAttr('data-original') || getAttr('data-lazy-src') || getAttr('data-image') || getAttr('data-url');
            if (!src) return '';
            if (src.startsWith('//')) src = 'https:' + src;
            if (src.startsWith('http:')) src = src.replace('http:', 'https:');
            try { src = encodeURI(src); } catch { }
            return `<img src="${src}" style="max-width:100%;height:auto;display:block;margin:0 auto;" referrerpolicy="no-referrer" crossorigin="anonymous" />`;
        });
        return safe;
    }, [data]);

    // Translation to English when description contains CJK
    const [translatedDesc, setTranslatedDesc] = useState("");
    const translateToEnglish = async (text) => {
        try {
            const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=en&q=${encodeURIComponent(text)}`;
            const res = await fetch(url);
            const json = await res.json();
            return Array.isArray(json?.[0]) ? json[0].map((x) => x?.[0]).join(' ') : text;
        } catch {
            return text;
        }
    };
    useEffect(() => {
        const text = descriptionText || '';
        if (!text) { setTranslatedDesc(''); return; }
        if (!containsCJK(text)) { setTranslatedDesc(text); return; }
        let cancelled = false;
        (async () => {
            try {
                const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=en&q=${encodeURIComponent(text)}`;
                const res = await fetch(url);
                const json = await res.json();
                const translated = Array.isArray(json?.[0]) ? json[0].map((x) => x?.[0]).join(' ') : '';
                if (!cancelled) setTranslatedDesc(translated || text);
            } catch {
                if (!cancelled) setTranslatedDesc(text);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [descriptionText]);

    // Ensure first loadable image is shown in main viewer
    useEffect(() => {
        if (!images || !images.length) return;
        let cancelled = false;
        const tryIndex = (i) => {
            if (i >= images.length) return;
            const probe = new Image();
            probe.referrerPolicy = 'no-referrer';
            probe.crossOrigin = 'anonymous';
            probe.onload = () => { if (!cancelled) setActiveImageIdx(i); };
            probe.onerror = () => tryIndex(i + 1);
            probe.src = images[i];
        };
        tryIndex(0);
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images.length]);

    // Attribute helpers
    const attrEntries = useMemo(() => attributes.map(a => ({
        name: (a?.nameTrans || a?.name || a?.attributeName || '').toString().toLowerCase(),
        value: getBestText(a?.valueTrans, a?.value, a?.attributeValue)
    })), [attributes]);

    const findAttrValue = useMemo(() => {
        return (keywords) => {
            for (const { name, value } of attrEntries) {
                if (keywords.some(k => name.includes(k))) return value;
            }
            return '';
        };
    }, [attrEntries]);

    // Key product info
    const productInfo = useMemo(() => {
        return {
            title: getBestText(data?.subjectTrans, data?.subject, "Product Title"),
            price: data?.priceInfo?.price || 'Contact for price',
            moq: data?.minOrderQuantity || findAttrValue(['moq', 'minimum order']) || 'Contact supplier',
            monthlySold: data?.monthSold || 'N/A',
            supplier: getBestText(data?.companyNameTrans, data?.companyName),
            location: getBestText(data?.countryTrans, data?.country, data?.location),
            brand: getBestText(findAttrValue(['brand name', 'brand']), data?.brand),
            model: getBestText(findAttrValue(['model', 'model number']), data?.model),
            origin: getBestText(findAttrValue(['place of origin', 'origin']), data?.origin, data?.country),
        };
    }, [data, findAttrValue]);

    // Packaging & delivery
    const packagingInfo = useMemo(() => {
        return {
            sellingUnits: getBestText(findAttrValue(['selling units', 'units']), 'Single item'),
            packageSize: getBestText(findAttrValue(['single package size', 'package size', 'dimension', 'size'])),
            grossWeight: getBestText(findAttrValue(['single gross weight', 'gross weight', 'weight'])),
        };
    }, [findAttrValue]);

    // Lead time table (try to derive days text)
    const leadTime = useMemo(() => {
        const ltStr = getBestText(findAttrValue(['lead time', 'delivery time', 'days']));
        if (ltStr && ltStr !== 'N/A') {
            return [
                { qty: '1 - 10000', days: ltStr },
                { qty: '> 10000', days: 'To be negotiated' },
            ];
        }
        return [];
    }, [findAttrValue]);

    // Customization options
    const customization = useMemo(() => {
        return {
            logo: /custom/i.test(findAttrValue(['customized logo', 'logo customization', 'logo'])),
            packaging: /custom/i.test(findAttrValue(['customized packaging', 'packaging customization'])),
            graphic: /custom/i.test(findAttrValue(['graphic customization', 'graphic'])),
            material: /custom/i.test(findAttrValue(['material customization', 'material'])),
            structure: /custom/i.test(findAttrValue(['structure customization', 'structure'])),
        };
    }, [findAttrValue]);

    // Helpers to render unknown objects/arrays into readable tables
    const isPrimitive = (v) => v === null || v === undefined || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
    const getTrans = (obj, key) => {
        if (!obj || typeof obj !== 'object') return undefined;
        return obj[key] ?? obj[key?.toLowerCase?.()] ?? obj[key?.toUpperCase?.()];
    };
    const toKeyValueRows = (source) => {
        if (!source) return [];
        // If array of {name,value} or {key,value}
        if (Array.isArray(source)) {
        const rows = [];
            source.forEach((item, idx) => {
                if (item && typeof item === 'object') {
                    const name = getTrans(item, 'nameTrans');
                    const val = getTrans(item, 'valueTrans');
                    if (name !== undefined || val !== undefined) {
                        rows.push({ key: String(name ?? ''), value: String(val ?? '') });
                    } else {
                        // If no trans fields, avoid [object Object] noise by skipping or showing primitives only
                        Object.entries(item).forEach(([k, v]) => {
                            if (/name(trans)?/i.test(k) || /value(trans)?/i.test(k)) return; // already handled
                            if (isPrimitive(v)) rows.push({ key: k, value: String(v) });
                        });
                        if (rows.length === 0) rows.push({ key: `Item ${idx + 1}`, value: '' });
                    }
                } else if (isPrimitive(item)) {
                    rows.push({ key: `Item ${idx + 1}`, value: String(item) });
                }
            });
            return rows;
        }
        // Object: flatten one level of primitives
        if (typeof source === 'object') {
            const n = getTrans(source, 'nameTrans');
            const v = getTrans(source, 'valueTrans');
            if (n !== undefined || v !== undefined) return [{ key: String(n ?? ''), value: String(v ?? '') }];
            return Object.entries(source)
                .filter(([, v]) => isPrimitive(v) || (Array.isArray(v) && v.every(isPrimitive)))
                .map(([k, v]) => ({ key: k, value: Array.isArray(v) ? v.join(', ') : String(v) }));
        }
        return [{ key: 'value', value: String(source) }];
    };
    const KeyValueTable = ({ obj, translate = false }) => {
        const baseRows = useMemo(() => toKeyValueRows(obj), [obj]);
        const [rows, setRows] = useState(baseRows);
        useEffect(() => setRows(baseRows), [baseRows]);
        useEffect(() => {
            if (!translate) return;
            const needTranslate = baseRows.some(r => containsCJK(r.key) || containsCJK(r.value));
            if (!needTranslate) return;
            let cancelled = false;
            (async () => {
                const translated = await Promise.all(baseRows.map(async (r) => ({
                    key: containsCJK(r.key) ? await translateToEnglish(r.key) : r.key,
                    value: containsCJK(r.value) ? await translateToEnglish(r.value) : r.value,
                })));
                if (!cancelled) setRows(translated);
            })();
            return () => { cancelled = true; };
        }, [translate, baseRows]);
        if (!rows.length) return <p className="text-muted">No data available</p>;
        return (
            <div className="table-responsive">
                <table className="table table-striped table-sm align-middle mb-0">
                    <thead>
                        <tr>
                            <th style={{ width: '35%' }}>Field</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx}>
                                <td className="text-muted" style={{ wordBreak: 'break-all' }}>{row.key}</td>
                                <td style={{ wordBreak: 'break-word' }}>{row.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Initialize HLS if needed and when video becomes active
    useEffect(() => {
        try {
            const url = isVideoActive ? (videos?.[0] || '') : '';
            if (!videoRef.current) return;
            const video = videoRef.current;
            if (!url) { video.removeAttribute('src'); video.load?.(); return; }
            const isHls = /\.m3u8(\?|$)/i.test(url);
            if (!isHls) { video.src = url; video.play?.(); return; }
            if (video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl')) { video.src = url; video.play?.(); return; }
            let hlsInstance;
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
            script.async = true;
            script.onload = () => {
                try {
                    if (window.Hls && window.Hls.isSupported()) {
                        hlsInstance = new window.Hls();
                        hlsInstance.loadSource(url);
                        hlsInstance.attachMedia(video);
                        video.play?.();
                    }
                } catch { }
            };
            script.onerror = () => { };
            document.body.appendChild(script);
            return () => {
                try { hlsInstance && hlsInstance.destroy(); } catch { }
                try { document.body.removeChild(script); } catch { }
            };
        } catch { }
    }, [videos, isVideoActive]);

    if (loading) {
        return (
            <div className="container py-5">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="mt-3">Loading product details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="container py-5">
                <div className="alert alert-warning">No product details found</div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Breadcrumb */}
            <div className="container mb-3">
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <button className="btn btn-link p-0 text-decoration-none" onClick={() => navigate(-1)}>
                                ← Back to Products
                            </button>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">{productInfo.title}</li>
                    </ol>
                </nav>
            </div>

            <div className="container" style={{ maxWidth: 1200 }}>
                <div className="row g-4">
                    {/* Left Column - Product Images (Amazon Style) */}
                    <div className="col-lg-6">
                        <div className="d-flex">
                            {/* Thumbnail Gallery (Left Side) */}
                            <div className="me-3" style={{ width: 80 }}>
                                {images?.length > 0 && (
                                    <div className="d-flex flex-column gap-2">
                                        {effectiveImages.slice(0, 8).map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={img}
                                                alt={`${productInfo.title} - ${idx + 1}`}
                                                onClick={() => { setIsVideoActive(false); setActiveImageIdx(idx); }}
                                                className="img-thumbnail"
                                                style={{
                                                    width: 80,
                                                    height: 80,
                                                    objectFit: "cover",
                                                    cursor: "pointer",
                                                    border: idx === activeImageIdx && !isVideoActive ? "2px solid #ff6a00" : "1px solid #dee2e6",
                                                }}
                                                referrerPolicy="no-referrer"
                                                crossOrigin="anonymous"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ))}
                                        {videos.length > 0 && (
                                            <div className="position-relative" onClick={() => setIsVideoActive(true)}>
                                                {(effectiveImages[0]) ? (
                                                <img
                                                        src={effectiveImages[0]}
                                                    alt="Video thumbnail"
                                                    className="img-thumbnail"
                                                        style={{ width: 80, height: 80, objectFit: "cover", cursor: "pointer", border: isVideoActive ? "2px solid #ff6a00" : "1px solid #dee2e6" }}
                                                        onError={(e)=>{ e.currentTarget.style.display='none'; }}
                                                    />
                                                ) : (
                                                    <div className="bg-dark d-flex align-items-center justify-content-center img-thumbnail" style={{ width: 80, height: 80, cursor: 'pointer', border: isVideoActive ? "2px solid #ff6a00" : "1px solid #dee2e6" }}>
                                                        <span className="text-white small">Video</span>
                                                    </div>
                                                )}
                                                <div className="position-absolute top-50 start-50 translate-middle">
                                                    <i className="fas fa-play-circle text-white fs-4"></i>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Main Image Display */}
                            <div className="flex-grow-1">
                                <div className="border rounded bg-white d-flex align-items-center justify-content-center overflow-hidden" style={{ height: 520 }}>
                                    {isVideoActive && videos.length > 0 ? (
                                        <video ref={videoRef} controls autoPlay className="w-100 h-100" style={{ objectFit: 'contain' }} poster={effectiveImages[0] || undefined} />
                                    ) : effectiveImages?.length ? (
                                        <img
                                            src={effectiveImages[Math.min(activeImageIdx, effectiveImages.length - 1)]}
                                            alt={productInfo.title}
                                            className="w-100 h-100"
                                            style={{ objectFit: 'contain', display: 'block' }}
                                            referrerPolicy="no-referrer"
                                            crossOrigin="anonymous"
                                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = noImage; }}
                                        />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height: '100%' }}>
                                            <span className="text-muted">No Image Available</span>
                                        </div>
                                    )}
                                </div>
                                <div className="text-center mt-2">
                                    <small className="text-primary">Click to see full view</small>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>

                {/* Product Details Sections */}
                <div className="row mt-5">
                    <div className="col-12">
                        {/* Alibaba-like blocks: Key attributes, Packaging & delivery, Lead time, Customization, Protections */}
                        <div className="bg-white rounded shadow-sm p-4 mb-4">
                            <h5 className="mb-3">Key attributes</h5>
                            {Array.isArray(attributes) && attributes.length > 0 ? (
                                <div className="row g-3">
                                    {attributes.slice(0, 12).map((attr, idx) => (
                                        <div className="col-md-6" key={idx}>
                                            <div className="d-flex border rounded p-2 h-100">
                                                <div style={{ width: 200 }} className="text-muted">
                                                    {getBestText(attr?.nameTrans, attr?.name, attr?.attributeName)}
                                                </div>
                                                <div className="flex-1">
                                                    {getBestText(attr?.valueTrans, attr?.value, attr?.attributeValue)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-muted">No attributes available</div>
                            )}
                            {(() => {
                              const skuInfos = Array.isArray(data?.productSkuInfos) ? data.productSkuInfos : (Array.isArray(data?.productSKUInfos) ? data.productSKUInfos : (Array.isArray(data?.skuInfos) ? data.skuInfos : []));
                              if (!skuInfos.length) return null;
                              return (
                              <div className="mt-3">
                                <h6>Variants</h6>
                                <div className="d-flex flex-wrap gap-2">
                                  {skuInfos.map((v, i) => (
                                    <span key={i} className="badge bg-light text-dark border">
                                      {(() => {const names=[]; if(v.color) names.push(v.color); if(v.size) names.push(v.size); if(Array.isArray(v?.skuAttributes)) v.skuAttributes.forEach(a=>{ if(a?.valueTrans||a?.value) names.push(a?.valueTrans||a?.value);}); return names.join(' / ');})()}
                                      {v.price ? ` — ¥${String(v.price).match(/\d+[\d,]*(?:\.\d+)?/)?.[0]?.replace(/,/g,'')}` : ''}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              );
                            })()}
                        </div>

                        {(packagingInfo.packageSize || packagingInfo.grossWeight) && (
                            <div className="bg-white rounded shadow-sm p-4 mb-4">
                                <h5 className="mb-3">Packaging and delivery</h5>
                                <div className="border rounded">
                                    <div className="d-flex py-2 border-bottom px-2">
                                        <div style={{ width: 200 }} className="text-muted">Selling Units</div>
                                        <div className="flex-1">{packagingInfo.sellingUnits}</div>
                                    </div>
                                    <div className="d-flex py-2 border-bottom px-2">
                                        <div style={{ width: 200 }} className="text-muted">Single package size</div>
                                        <div className="flex-1">{packagingInfo.packageSize || 'To be negotiated'}</div>
                                    </div>
                                    <div className="d-flex py-2 px-2">
                                        <div style={{ width: 200 }} className="text-muted">Single gross weight</div>
                                        <div className="flex-1">{packagingInfo.grossWeight || 'To be negotiated'}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {leadTime.length > 0 && (
                            <div className="bg-white rounded shadow-sm p-4 mb-4">
                                <h5 className="mb-3">Lead time</h5>
                                <div className="table-responsive">
                                    <table className="table table-bordered align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>Quantity (pieces)</th>
                                                <th>Lead time (days)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leadTime.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td>{row.qty}</td>
                                                    <td>{row.days}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {(customization.logo || customization.packaging || customization.graphic || customization.material || customization.structure) && (
                            <div className="bg-white rounded shadow-sm p-4 mb-4">
                                <h5 className="mb-3">Customization options</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    {customization.logo && <span className="badge bg-light text-dark border">Customized logo</span>}
                                    {customization.packaging && <span className="badge bg-light text-dark border">Customized packaging</span>}
                                    {customization.graphic && <span className="badge bg-light text-dark border">Graphic customization</span>}
                                    {customization.material && <span className="badge bg-light text-dark border">Material</span>}
                                    {customization.structure && <span className="badge bg-light text-dark border">Structure</span>}
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded shadow-sm p-4 mb-4">
                            <h5 className="mb-2">Protections for this product</h5>
                            <ul className="mb-0">
                                <li>Delivery via: Expect your order to be delivered before scheduled dates or receive a 10% delay compensation</li>
                                <li>Secure payments: SSL encryption and PCI DSS data protection</li>
                                <li>Standard refund policy: Refund if your order doesn't ship, is missing, or arrives with issues</li>
                            </ul>
                        </div>

                        {/* Tabs area */}
                        <div className="bg-white rounded shadow-sm">
                            {/* Tabs Navigation */}
                            <ul className="nav nav-tabs" id="productTabs" role="tablist">
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link active" id="description-tab" data-bs-toggle="tab" data-bs-target="#description" type="button" role="tab">
                                        Product Description
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="specifications-tab" data-bs-toggle="tab" data-bs-target="#specifications" type="button" role="tab">
                                        Specifications
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="media-tab" data-bs-toggle="tab" data-bs-target="#media" type="button" role="tab">
                                        Media
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="product-attr-tab" data-bs-toggle="tab" data-bs-target="#product-attr" type="button" role="tab">
                                        productAttribute
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="sale-info-tab" data-bs-toggle="tab" data-bs-target="#sale-info" type="button" role="tab">
                                        productSaleInfo
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="shipping-info-tab" data-bs-toggle="tab" data-bs-target="#shipping-info" type="button" role="tab">
                                        productShippingInfo
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="tags-tab" data-bs-toggle="tab" data-bs-target="#tags" type="button" role="tab">
                                        tagInfoList
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button className="nav-link" id="seller-data-tab" data-bs-toggle="tab" data-bs-target="#seller-data" type="button" role="tab">
                                        sellerDataInfo
                                    </button>
                                </li>
                            </ul>

                            {/* Tab Content */}
                            <div className="tab-content p-4" id="productTabsContent" style={{ minHeight: 400 }}>
                                {/* Description Tab */}
                                <div className="tab-pane fade show active" id="description" role="tabpanel">
                                    {descriptionHtml ? (
                                        <div
                                            style={{ lineHeight: '1.8', fontSize: 14 }}
                                            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                                        />
                                    ) : descriptionText ? (
                                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: 14 }}>
                                            {descriptionText}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No description available</p>
                                    )}
                                </div>

                                {/* Specifications Tab */}
                                <div className="tab-pane fade" id="specifications" role="tabpanel">
                                    {Array.isArray(attributes) && attributes.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Attribute</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {attributes.filter((attr) => {
                                                        const label = (getBestText(attr?.nameTrans, attr?.name, attr?.attributeName) || '').toLowerCase();
                                                        // Hide Supplierlogo and Consign Price
                                                        if (label.includes('supplierlogo') || label.includes('supplier logo')) return false;
                                                        if (label.includes('consign price') || label.includes('consignprice')) return false;
                                                        return true;
                                                    }).map((attr, idx) => (
                                                        <tr key={idx}>
                                                            <td className="fw-semibold">
                                                                {getBestText(attr?.nameTrans, attr?.name, attr?.attributeName)}
                                                            </td>
                                                            <td>
                                                                {getBestText(attr?.valueTrans, attr?.value, attr?.attributeValue)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-muted">No specifications available</p>
                                    )}
                                </div>

                                {/* Media Tab */}
                                <div className="tab-pane fade" id="media" role="tabpanel">
                                    {images.length === 0 && videos.length === 0 ? (
                                        <p className="text-muted">No media available</p>
                                    ) : (
                                        <div>
                                            {images.length > 0 && (
                                                <>
                                                    <h6 className="mb-3">Images</h6>
                                                    <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-2">
                                                        {images.map((img, idx) => (
                                                            <div key={idx} className="col">
                                                                <a href={img} target="_blank" rel="noreferrer" className="d-block">
                                                                    <img
                                                                        src={img}
                                                                        alt={`Image ${idx + 1}`}
                                                                        className="img-fluid rounded border"
                                                                        style={{ objectFit: 'cover', width: '100%', height: 140 }}
                                                                        referrerPolicy="no-referrer"
                                                                        crossOrigin="anonymous"
                                                                        onError={(e)=>{ e.currentTarget.style.display='none'; }}
                                                                    />
                                                                </a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {videos.length > 0 && (
                                                <div className="mt-4">
                                                    <h6 className="mb-3">Videos</h6>
                                                    <div className="ratio ratio-16x9" style={{ maxWidth: 720 }}>
                                                        <video ref={videoRef} controls className="w-100 h-100" src={!/\.m3u8(\?|$)/i.test(videos[0]) ? videos[0] : undefined} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* productAttribute Tab */}
                                <div className="tab-pane fade" id="product-attr" role="tabpanel">
                                    {(() => {
                                        const candidates = [
                                            data?.productAttribute,
                                            data?.productAttr,
                                            data?.product_attributes,
                                            data?.productAttributeList,
                                            data?.productAttrList,
                                            data?.productAttributes,
                                        ];
                                        const first = candidates.find((c)=> c && (Array.isArray(c) ? c.length : Object.keys(c||{}).length));
                                        let objToShow = first;
                                        if (first && !Array.isArray(first) && typeof first === 'object') {
                                            objToShow = Object.entries(first).map(([k,v])=> ({ nameTrans: String(k), valueTrans: String(v) }));
                                        }
                                        if (Array.isArray(objToShow)) {
                                            objToShow = objToShow.map((it)=> {
                                                if (!it || typeof it !== 'object') return it;
                                                const n = getTrans(it,'nameTrans') ?? it.name ?? it.attributeName ?? it.key ?? '';
                                                const v = getTrans(it,'valueTrans') ?? it.value ?? it.attributeValue ?? it.val ?? '';
                                                return { nameTrans: n, valueTrans: v };
                                            });
                                        }
                                        return <KeyValueTable obj={objToShow} translate />;
                                    })()}
                                </div>

                                {/* productSaleInfo Tab */}
                                <div className="tab-pane fade" id="sale-info" role="tabpanel">
                                    <KeyValueTable obj={data?.productSaleInfo || data?.saleInfo} />
                                </div>

                                {/* productShippingInfo Tab */}
                                <div className="tab-pane fade" id="shipping-info" role="tabpanel">
                                    <KeyValueTable obj={data?.productShippingInfo || data?.shippingInfo} />
                                </div>

                                {/* tagInfoList Tab */}
                                <div className="tab-pane fade" id="tags" role="tabpanel">
                                    {(() => {
                                        const raw = (Array.isArray(data?.tagInfoList) ? data.tagInfoList : []).concat(Array.isArray(data?.targetInfoList) ? data.targetInfoList : []);
                                        const normalized = raw.map((t) => {
                                            if (t && typeof t === 'object') {
                                                const name = getTrans(t, 'nameTrans') ?? t.name ?? t.tag ?? t.label;
                                                const value = getTrans(t, 'valueTrans') ?? t.value ?? t.val ?? '';
                                                return { nameTrans: name, valueTrans: value };
                                            }
                                            if (isPrimitive(t)) return { nameTrans: String(t), valueTrans: '' };
                                            return null;
                                        }).filter(Boolean);
                                        return <KeyValueTable obj={normalized} translate />;
                                    })()}
                                </div>

                                {/* sellerDataInfo Tab */}
                                <div className="tab-pane fade" id="seller-data" role="tabpanel">
                                    <KeyValueTable obj={data?.sellerDataInfo || data?.sellerInfo || data?.company || data?.supplier} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


